import {
  Button,
  ContentHeader,
  Css,
  EntityLockup,
  Icon,
  IconButton,
  IconKey,
  Palette,
  Tag,
  useComputed,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useMemo } from "react";
import { useRouteParams } from "src/hooks/useRouteParams";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { StatTotals } from "src/routes/projects/change-events/guided/components/StatTotals";
import { imageUrl } from "src/routes/projects/change-events/guided/workflows/components/optionCardUtils";
import {
  formatOptionPrice,
  optionAmountInCents,
  OptionPrice,
  OptionPriceMode,
} from "src/routes/projects/change-events/guided/workflows/components/OptionPrice";
import { ReviewImpactedPrice } from "src/routes/projects/change-events/guided/workflows/components/ReviewImpactedPrice";
import {
  DiscontinuedOptionsStep_ConfigChangeFragment,
  useDiscontinuedOptionsStepOriginalQuery,
  useDiscontinuedOptionsStepQuery,
} from "src/routes/projects/change-events/guided/workflows/configurationTrueUp/2.DiscontinuedOptionsStep.generated";
import { ConfigurationTrueUpFormInput } from "src/routes/projects/change-events/guided/workflows/configurationTrueUp/configurationTrueUpWorkflow";
import {
  createDiscontinuedOptionGroups,
  createDiscontinuedOptionsRows,
  hasDroppedToggle,
  mapDiscontinuedOptionsRowsToForm,
  mapDiscontinuedOptionsRowsToInput,
  OptionGroupChangeRow,
  toggleDiscontinuedOptionsKept,
  useSaveDiscontinuedOptions,
} from "src/routes/projects/change-events/guided/workflows/configurationTrueUp/discontinuedOptionsUtils";
import { toSelectOptionsSections } from "src/routes/projects/change-events/guided/workflows/sharedSteps/selectOptionsUtils";
import { SelectOptionsUtils_OptionGroupFragment } from "src/routes/projects/change-events/guided/workflows/sharedSteps/selectOptionsUtils.generated";
import { ProjectParams } from "src/routes/routesDef";
import { fail, queriesResult } from "src/utils";
import { PRODUCT_FALLBACK_IMG_URL } from "src/utils/constants";

type DiscontinuedOptionsPaneProps = { formState: ObjectState<ConfigurationTrueUpFormInput> };

/**
 * The right pane's list of what happens to each discontinued selection: kept as a custom option, replaced with an
 * alternative, or removed. Each row's X keeps or drops it, and "Clear all changes" goes back to keeping every one.
 */
export function DiscontinuedOptionsPane(props: DiscontinuedOptionsPaneProps) {
  const { formState } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  const changeEventId = useComputed(() => formState.id.value, [formState]);
  const variables = {
    changeEventId: changeEventId ?? fail("Configuration True-Up draft was not created"),
    structureId: projectId,
  };
  const query = useDiscontinuedOptionsStepQuery({ variables, fetchPolicy: "cache-first" });
  // Fetch `original` with `no-cache`, like the step: it shares option group and option ids with `result`, so caching
  // both in Apollo would let `original`'s option groups overwrite `result`'s.
  const originalQuery = useDiscontinuedOptionsStepOriginalQuery({ variables, fetchPolicy: "no-cache" });
  return queriesResult([query, originalQuery] as const, {
    data: ({ changeEvent }, originalData) => {
      const configChange = changeEvent.configChange ?? fail("Change event has no config change");
      const original = originalData.changeEvent.configChange?.original ?? fail("Change event has no config change");
      return (
        <DiscontinuedOptionsPaneView
          formState={formState}
          configChange={configChange}
          originalGroups={original.config.optionGroups}
        />
      );
    },
  });
}

type DiscontinuedOptionsPaneViewProps = {
  formState: ObjectState<ConfigurationTrueUpFormInput>;
  configChange: DiscontinuedOptionsStep_ConfigChangeFragment;
  originalGroups: SelectOptionsUtils_OptionGroupFragment[];
};

function DiscontinuedOptionsPaneView(props: DiscontinuedOptionsPaneViewProps) {
  const { formState, configChange, originalGroups } = props;
  const { result, optionGroupChanges } = configChange;
  const tid = useTestIds(props, "discontinuedOptions");
  const { openModal } = useModal();
  const rows = useMemo(() => createDiscontinuedOptionsRows(optionGroupChanges), [optionGroupChanges]);
  const groups = useMemo(
    () => createDiscontinuedOptionGroups(rows, result.config.optionGroups, originalGroups),
    [rows, result, originalGroups],
  );
  const { save, saving } = useSaveDiscontinuedOptions(formState);
  const { optionIds, droppedIds, priceMode } = useComputed(
    () => ({
      optionIds: formState.configChange.optionIds.value ?? [],
      droppedIds: formState.configChange.droppedOptionIds.value ?? [],
      priceMode: formState.priceMode.value ?? OptionPriceMode.Price,
    }),
    [formState],
  );
  const isPrice = priceMode === OptionPriceMode.Price;

  const changes = toPaneChanges(rows, groups, optionIds, droppedIds, priceMode);
  const updated = changes.count((change) => change.type !== "Custom");
  const groupByRpogId = groups.keyBy((ccfgog) => ccfgog.group.id);
  // A child group's row (e.g. a personalization variant) shows under its parent's section, like its card
  const changesBySectionId = changes.groupBy((change) => change.group.parentGroup?.id ?? change.group.group.id);
  const sections = toSelectOptionsSections(groups, result.interiorSubSteps)
    .map((section) => ({
      title: section.title,
      changes: section.groups.flatMap((group) => changesBySectionId[group.group.id] ?? []),
    }))
    .filter((section) => section.changes.nonEmpty);

  /** Saves the selection with `nextDroppedIds` dropped, the same way the step saves when a card changes. */
  async function saveDropped(nextDroppedIds: string[]) {
    const formRows = mapDiscontinuedOptionsRowsToForm({ groups, optionIds }, rows, nextDroppedIds).rows;
    await save(mapDiscontinuedOptionsRowsToInput(formState, formRows, groups, rows), nextDroppedIds);
  }

  function clearAll() {
    openModal({
      content: (
        <ConfirmationModal
          title="Clear all changes?"
          confirmationMessage="Every discontinued selection will be kept as a custom option again, like when this step started. You'll need to replace or remove them again if you still want to."
          label="Clear all"
          danger
          onConfirmAction={() => saveDropped([])}
        />
      ),
    });
  }

  return (
    <div css={Css.df.fdc.gap3.$} {...tid}>
      <StatTotals
        testId="discontinuedOptionsTotals"
        items={[
          { value: <span css={Css.if(updated > 0).blue600.$}>{updated}</span>, label: "Updated", testId: "updated" },
          { value: changes.count((change) => change.type === "Custom"), label: "Kept as custom", testId: "kept" },
          {
            value: (
              <ReviewImpactedPrice
                valueInCents={changes.sum((change) => change.changeAmountInCents)}
                hideDisplayDirection={!isPrice}
              />
            ),
            label: priceMode === OptionPriceMode.Cost ? "Cost Δ from Previous" : "$Δ from Previous",
            testId: "delta",
          },
        ]}
      />
      {changes.isEmpty && (
        <div css={Css.sm.gray700.tac.$} {...tid.emptyState}>
          No selections were discontinued in the latest Product Offering.
        </div>
      )}
      {sections.map((section) => (
        <div key={section.title} css={Css.df.fdc.gap2.$}>
          {/* Inside the pane the header must not take the page's horizontal padding */}
          <ContentHeader title={section.title} level={4} xss={Css.px0.$} />
          {section.changes.map((change) => (
            <ChangeRow
              key={change.row.rpoId}
              change={change}
              displayDirection={isPrice}
              disabled={saving || requiresReason(change, rows, droppedIds, groupByRpogId)}
              onToggle={() => saveDropped(toggleDiscontinuedOptionsKept(rows, droppedIds, change.row))}
            />
          ))}
        </div>
      ))}
      {droppedIds.nonEmpty && (
        <div css={Css.df.jcc.$}>
          <Button variant="tertiary" label="Clear all changes" disabled={saving} onClick={clearAll} />
        </div>
      )}
    </div>
  );
}

type ChangeRowProps = {
  change: PaneChange;
  displayDirection: boolean;
  disabled: boolean | string;
  onToggle: () => void;
};

/**
 * One discontinued selection: thumbnail, group name, the option it has now, what happened to it, its price delta and an
 * X to drop it (when kept) or keep it again. A removed row is muted.
 */
function ChangeRow(props: ChangeRowProps) {
  const { change, displayDirection, disabled, onToggle } = props;
  const { group, type, shown, originalAmountInCents, changeAmountInCents } = change;
  const title = group.group.name;
  const subtitle = shown.option.name !== title ? shown.option.name : undefined;
  return (
    <div css={Css.df.aic.gap1.$}>
      <div css={Css.fg1.mw0.if(type === "Removed").o50.$}>
        <EntityLockup
          compact
          imgSrc={imageUrl(shown) ?? PRODUCT_FALLBACK_IMG_URL}
          title={title}
          description={
            <div css={Css.df.fdc.gapPx(2).$}>
              {subtitle && <div>{subtitle}</div>}
              <div css={Css.df.aic.gapPx(4).$}>
                <Icon icon={changeIcons[type].icon} color={changeIcons[type].color} inc={2} />
                {type}
              </div>
            </div>
          }
          right={
            <div css={Css.df.fdc.aife.$}>
              {type !== "Custom" && originalAmountInCents !== changeAmountInCents && (
                <Tag
                  type="strikethrough"
                  text={formatOptionPrice(originalAmountInCents, { displayDirection, trim: true })}
                />
              )}
              <OptionPrice priceInCents={changeAmountInCents} displayDirection={displayDirection} trim />
            </div>
          }
        />
      </div>
      <IconButton
        icon="x"
        label={type === "Custom" ? "Remove" : "Keep as custom option"}
        compact
        disabled={disabled}
        onClick={onToggle}
      />
    </div>
  );
}

const changeIcons: Record<PaneChange["type"], { icon: IconKey; color: Palette }> = {
  Custom: { icon: "wrench", color: Palette.Gray700 },
  Removed: { icon: "remove", color: Palette.Red600 },
  Updated: { icon: "refresh", color: Palette.Blue600 },
};

type PotentialOption = SelectOptionsUtils_OptionGroupFragment["options"][number];

type PaneChange = {
  row: OptionGroupChangeRow;
  group: SelectOptionsUtils_OptionGroupFragment;
  type: "Custom" | "Removed" | "Updated";
  /** The option the row shows: the kept or removed original, or what replaced it. */
  shown: PotentialOption;
  /** The original option's price (or cost), at what the customer reserved it for. */
  originalAmountInCents: number;
  /** The change in option price (or cost): 0 when kept, else the replacement's price less the original's. */
  changeAmountInCents: number;
};

/**
 * One change per row:
 * - kept: "Custom". Skipped for an option that depends on another one, since it isn't discontinued itself;
 * - dropped, with nothing else selected in its group: "Removed";
 * - dropped, with another option selected in its group: "Updated".
 */
function toPaneChanges(
  rows: OptionGroupChangeRow[],
  groups: SelectOptionsUtils_OptionGroupFragment[],
  optionIds: string[],
  droppedIds: string[],
  priceMode: OptionPriceMode,
): PaneChange[] {
  const groupByRpogId = groups.keyBy((ccfgog) => ccfgog.group.id);
  const optionIdSet = new Set(optionIds);
  return rows
    .map((row): PaneChange | undefined => {
      const group = groupByRpogId[row.rpogId];
      const original = group?.options.find((ccfgpo) => ccfgpo.option.id === row.rpoId);
      if (!group || !original) return undefined;
      const originalAmountInCents = optionAmountInCents(original, priceMode);
      if (!droppedIds.includes(row.rpoId)) {
        if (row.prerequisite) return undefined;
        return { row, group, type: "Custom", shown: original, originalAmountInCents, changeAmountInCents: 0 };
      }
      const current = group.options.find((ccfgpo) => optionIdSet.has(ccfgpo.option.id));
      const currentAmountInCents = current ? optionAmountInCents(current, priceMode) : 0;
      return {
        row,
        group,
        type: current ? "Updated" : "Removed",
        shown: current ?? original,
        originalAmountInCents,
        changeAmountInCents: currentAmountInCents - originalAmountInCents,
      };
    })
    .compact();
}

/** A checkbox option can't be kept while the option it requires is dropped, same as on its card. */
function requiresReason(
  change: PaneChange,
  rows: OptionGroupChangeRow[],
  droppedIds: string[],
  groupByRpogId: Record<string, SelectOptionsUtils_OptionGroupFragment>,
): string | false {
  const { prerequisite } = change.row;
  if (!prerequisite || !droppedIds.includes(prerequisite.rpoId)) return false;
  const group = groupByRpogId[change.row.rpogId] ?? fail(`No group ${change.row.rpogId}`);
  return hasDroppedToggle(group, rows) && `Requires ‘${prerequisite.change.originalRpov?.name}’`;
}
